var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useCallback, useContext, useEffect, useMemo, } from "react";
import useService from "./useService";
import NoticeService from "../../Services/Notice.service";
import useCurrentApiUser from "./useCurrentApiUser";
import { debounce } from "../../../util/debounce";
const NoticeContext = createContext(
// @ts-expect-error this will be defined right away, so we'll ignore it's an empty object for now.
{});
export const NoticeContextProvider = ({ children }) => {
    const person = useCurrentApiUser();
    const [notices, loadNotice, noticeActions] = useService(NoticeService.getNoticesForPerson);
    useEffect(() => {
        if (person === null || person === void 0 ? void 0 : person.id) {
            loadNotice(person.id);
        }
    }, [loadNotice, person === null || person === void 0 ? void 0 : person.id]);
    const _refreshNotices = useMemo(() => () => {
        if (person === null || person === void 0 ? void 0 : person.id) {
            debounce(loadNotice, 10000);
        }
    }, [loadNotice, person === null || person === void 0 ? void 0 : person.id]);
    const _hasNotice = useMemo(() => (noticeType, populationId) => {
        var _a;
        if (!noticeType || !populationId) {
            return false;
        }
        return !!((_a = notices.data) === null || _a === void 0 ? void 0 : _a.some((notice) => notice.noticeType === noticeType && notice.key === populationId));
    }, [notices.data]);
    const _getNotice = useCallback((noticeType, populationId) => {
        var _a, _b;
        return ((_b = (_a = notices.data) === null || _a === void 0 ? void 0 : _a.find((notice) => {
            return (notice.noticeType === noticeType && notice.key === populationId);
        })) !== null && _b !== void 0 ? _b : undefined);
    }, [notices]);
    const _markSeen = useCallback((noticeType, populationId) => __awaiter(void 0, void 0, void 0, function* () {
        if (notices === null || notices === void 0 ? void 0 : notices.data) {
            const populationNotice = _getNotice(noticeType, populationId);
            if (populationNotice && (person === null || person === void 0 ? void 0 : person.id)) {
                noticeActions.edit((currentNotices) => {
                    if (currentNotices) {
                        // Remove the loaded notice from the current notices
                        return currentNotices.filter((n) => n.id !== populationNotice.id);
                    }
                    return [];
                });
                try {
                    // Try marking the notice as seen
                    yield NoticeService.markSeen(populationNotice.id, person.id);
                }
                catch (error) {
                    // If an error occurs, add the notice we removed
                    return noticeActions.edit((currentNotices) => {
                        if (currentNotices) {
                            return [...currentNotices, populationNotice];
                        }
                        return [];
                    });
                }
            }
        }
        return noticeActions;
    }), [_getNotice, noticeActions, notices === null || notices === void 0 ? void 0 : notices.data, person === null || person === void 0 ? void 0 : person.id]);
    const value = useMemo(() => ({
        markSeen: _markSeen,
        hasNotice: _hasNotice,
        getNotice: _getNotice,
        refreshNotices: _refreshNotices,
    }), [_getNotice, _hasNotice, _markSeen, _refreshNotices]);
    return (_jsx(NoticeContext.Provider, { value: value, children: children }));
};
const useNotices = (noticeType, populationId) => {
    const { markSeen, refreshNotices, hasNotice, getNotice } = useContext(NoticeContext);
    const _hasNotice = useMemo(() => hasNotice(noticeType, populationId), [hasNotice, noticeType, populationId]);
    return {
        markSeen: () => markSeen(noticeType, populationId),
        refreshNotices: () => refreshNotices(),
        hasNotice: _hasNotice,
        getNotice: () => getNotice(noticeType, populationId),
    };
};
export default useNotices;
